import { useState, useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useKidsStore } from '../../store/kidsStore';
import { toast } from 'sonner';

type Command = 'up' | 'down' | 'left' | 'right';
type DifficultadKey = 'neurona' | 'sinapsis' | 'cerebro' | 'genio' | 'einstein';

const LEVELS = {
  neurona: { nombre: 'Neurona', icono: '🧠', size: 5, estrellas: 0 },
  sinapsis: { nombre: 'Sinapsis', icono: '⚡', size: 7, estrellas: 1 },
  cerebro: { nombre: 'Cerebro', icono: '🧩', size: 9, estrellas: 2 },
  genio: { nombre: 'Genio', icono: '💡', size: 11, estrellas: 3 },
  einstein: { nombre: 'Einstein', icono: '🎓', size: 13, estrellas: 3 }
};

const WORLDS = [
  { code: 'granja', animal: '🐷', objetivo: '🏡' },
  { code: 'bosque', animal: '🦊', objetivo: '🏠' },
  { code: 'oceano', animal: '🐠', objetivo: '🪸' },
  { code: 'artico', animal: '🐧', objetivo: '🏔️' },
  { code: 'sabana', animal: '🦁', objetivo: '🌳' },
  { code: 'selva', animal: '🐵', objetivo: '🍌' }
];

const COMMAND_ARROWS: Record<Command, string> = {
  up: '↑',
  down: '↓',
  left: '←',
  right: '→'
};

const COMMAND_DELTAS: Record<Command, { dx: number; dy: number }> = {
  up: { dx: 0, dy: -1 },
  down: { dx: 0, dy: 1 },
  left: { dx: -1, dy: 0 },
  right: { dx: 1, dy: 0 }
};

interface Star {
  x: number;
  y: number;
  collected: boolean;
}

interface MazeData {
  size: number;
  grid: number[][];
  inicio: { x: number; y: number };
  meta: { x: number; y: number };
  estrellas: Star[];
  animal: string;
  objetivo: string;
}

export default function LaberintoGame() {
  const tenant = useTenant();
  const { selectedKid, registerProgress, registerPlayTime } = useKidsStore();

  const [nivel, setNivel] = useState<DifficultadKey>('neurona');
  const config = LEVELS[nivel];

  const [mazeData, setMazeData] = useState<MazeData | null>(null);
  const [sequence, setSequence] = useState<Command[]>([]);
  const [attemptsCount, setAttemptsCount] = useState(0);
  const [starsCollected, setStarsCollected] = useState(0);
  const [isExecuting, setIsExecuting] = useState(false);
  const [visitedCells, setVisitedCells] = useState<{ x: number; y: number }[]>([]);
  const [currentPosition, setCurrentPosition] = useState<{ x: number; y: number } | null>(null);
  const [gameWon, setGameWon] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    generateNewMaze();
  }, [nivel]);

  const generateNewMaze = () => {
    const size = config.size;
    const maze = generateMazeGrid(size);
    const grid = convertToGameGrid(maze, size);

    addExtraPaths(grid, size);

    const inicio = { x: 0, y: 0 };
    const meta = { x: size - 1, y: size - 1 };

    grid[inicio.y][inicio.x] = 0;
    grid[meta.y][meta.x] = 0;

    const estrellas = placeStars(grid, size, config.estrellas, inicio, meta);

    const world = WORLDS[Math.floor(Math.random() * WORLDS.length)];

    const newMaze: MazeData = {
      size,
      grid,
      inicio,
      meta,
      estrellas,
      animal: world.animal,
      objetivo: world.objetivo
    };

    if (!hasPath(grid, inicio, meta, size)) {
      generateNewMaze();
      return;
    }

    setMazeData(newMaze);
    setSequence([]);
    setAttemptsCount(0);
    setStarsCollected(0);
    setVisitedCells([]);
    setCurrentPosition(null);
    setGameWon(false);
  };

  const generateMazeGrid = (size: number): boolean[][] => {
    const maze = Array(size).fill(false).map(() => Array(size).fill(false));
    const visited = Array(size).fill(false).map(() => Array(size).fill(false));

    const recursiveBacktracking = (x: number, y: number) => {
      visited[y][x] = true;
      maze[y][x] = true;

      const directions = [
        { dx: 0, dy: -1 },
        { dx: 1, dy: 0 },
        { dx: 0, dy: 1 },
        { dx: -1, dy: 0 }
      ];

      for (let i = directions.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [directions[i], directions[j]] = [directions[j], directions[i]];
      }

      for (const dir of directions) {
        const nx = x + dir.dx;
        const ny = y + dir.dy;

        if (nx >= 0 && nx < size && ny >= 0 && ny < size && !visited[ny][nx]) {
          recursiveBacktracking(nx, ny);
        }
      }
    };

    recursiveBacktracking(0, 0);
    return maze;
  };

  const convertToGameGrid = (maze: boolean[][], size: number): number[][] => {
    const grid = Array(size).fill(0).map(() => Array(size).fill(0));

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        grid[y][x] = maze[y][x] ? 0 : 1;
      }
    }

    return grid;
  };

  const addExtraPaths = (grid: number[][], size: number) => {
    const extraPaths = Math.floor(size * 0.3);

    for (let i = 0; i < extraPaths; i++) {
      const x = Math.floor(Math.random() * (size - 2)) + 1;
      const y = Math.floor(Math.random() * (size - 2)) + 1;
      grid[y][x] = 0;
    }
  };

  const placeStars = (
    grid: number[][],
    size: number,
    numStars: number,
    inicio: { x: number; y: number },
    meta: { x: number; y: number }
  ): Star[] => {
    const stars: Star[] = [];
    let attempts = 0;

    while (stars.length < numStars && attempts < 100) {
      const x = Math.floor(Math.random() * (size - 2)) + 1;
      const y = Math.floor(Math.random() * (size - 2)) + 1;

      if (
        grid[y][x] === 0 &&
        (x !== inicio.x || y !== inicio.y) &&
        (x !== meta.x || y !== meta.y) &&
        !stars.some(s => s.x === x && s.y === y)
      ) {
        stars.push({ x, y, collected: false });
      }

      attempts++;
    }

    return stars;
  };

  const hasPath = (
    grid: number[][],
    inicio: { x: number; y: number },
    meta: { x: number; y: number },
    size: number
  ): boolean => {
    const visited = Array(size).fill(false).map(() => Array(size).fill(false));
    const queue: [number, number][] = [[inicio.x, inicio.y]];
    visited[inicio.y][inicio.x] = true;

    const directions = [[0, -1], [1, 0], [0, 1], [-1, 0]];

    while (queue.length > 0) {
      const [x, y] = queue.shift()!;

      if (x === meta.x && y === meta.y) return true;

      for (const [dx, dy] of directions) {
        const nx = x + dx;
        const ny = y + dy;

        if (nx >= 0 && nx < size && ny >= 0 && ny < size && !visited[ny][nx] && grid[ny][nx] === 0) {
          visited[ny][nx] = true;
          queue.push([nx, ny]);
        }
      }
    }

    return false;
  };

  const addCommand = (command: Command) => {
    if (!isExecuting && !gameWon) {
      setSequence([...sequence, command]);
    }
  };

  const removeCommand = (index: number) => {
    if (!isExecuting) {
      setSequence(sequence.filter((_, i) => i !== index));
    }
  };

  const clearSequence = () => {
    if (!isExecuting) {
      setSequence([]);
    }
  };

  const executeSequence = async () => {
    if (sequence.length === 0 || !mazeData || isExecuting) return;

    setIsExecuting(true);
    setAttemptsCount(attemptsCount + 1);

    let currentX = mazeData.inicio.x;
    let currentY = mazeData.inicio.y;
    let success = true;
    const newStars = [...mazeData.estrellas];
    const tempCollected: number[] = [];

    for (let i = 0; i < sequence.length; i++) {
      const command = sequence[i];
      const delta = COMMAND_DELTAS[command];
      const newX = currentX + delta.dx;
      const newY = currentY + delta.dy;

      await sleep(400);

      if (newX < 0 || newX >= mazeData.size || newY < 0 || newY >= mazeData.size) {
        toast.error('¡Te saliste del laberinto!');
        success = false;
        break;
      }

      if (mazeData.grid[newY][newX] === 1) {
        toast.error('¡Chocaste con una pared!');
        success = false;
        break;
      }

      currentX = newX;
      currentY = newY;
      setCurrentPosition({ x: currentX, y: currentY });

      if (!visitedCells.some(v => v.x === currentX && v.y === currentY)) {
        setVisitedCells(prev => [...prev, { x: currentX, y: currentY }]);
      }

      const starIndex = newStars.findIndex(s => s.x === currentX && s.y === currentY && !s.collected);
      if (starIndex !== -1) {
        tempCollected.push(starIndex);
      }

      if (currentX === mazeData.meta.x && currentY === mazeData.meta.y) {
        success = true;
        tempCollected.forEach(idx => {
          newStars[idx].collected = true;
        });
        setStarsCollected(starsCollected + tempCollected.length);
        setMazeData({ ...mazeData, estrellas: newStars });
        break;
      }
    }

    await sleep(500);

    if (success && currentX === mazeData.meta.x && currentY === mazeData.meta.y) {
      setGameWon(true);
      await saveProgress();
    } else {
      setCurrentPosition(null);
    }

    setIsExecuting(false);
  };

  const saveProgress = async () => {
    if (!selectedKid) return;

    try {
      const segundos = Math.floor((Date.now() - startTime) / 1000);

      await registerProgress(selectedKid.id, {
        juego_id: null,
        tipo_nivel: 'laberinto',
        nivel_id: Object.keys(LEVELS).indexOf(nivel) + 1,
        tablero_id: null,
        tiempo: segundos,
        movimientos: sequence.length
      });

      const minutosJugados = Math.ceil(segundos / 60);
      await registerPlayTime(selectedKid.id, minutosJugados);

      toast.success('¡Progreso guardado! 🎉');
    } catch (error) {
      console.error('Error al guardar:', error);
    }
  };

  const showHint = () => {
    if (sequence.length >= 3) {
      toast.info('💡 Ya tienes varios comandos. ¡Intenta ejecutar para ver si funcionan!');
      return;
    }

    if (!mazeData) return;

    const path = findPath();
    if (path && path.length > 0) {
      const hints: Record<Command, string> = {
        up: 'arriba ↑',
        down: 'abajo ↓',
        left: 'izquierda ←',
        right: 'derecha →'
      };
      toast.info(`💡 Pista: Intenta moverte hacia ${hints[path[0]]}`);
    }
  };

  const findPath = (): Command[] | null => {
    if (!mazeData) return null;

    const queue: [number, number, Command[]][] = [[mazeData.inicio.x, mazeData.inicio.y, []]];
    const visited = new Set<string>();
    visited.add(`${mazeData.inicio.x},${mazeData.inicio.y}`);

    while (queue.length > 0) {
      const [x, y, path] = queue.shift()!;

      if (x === mazeData.meta.x && y === mazeData.meta.y) {
        return path;
      }

      for (const [cmd, delta] of Object.entries(COMMAND_DELTAS) as [Command, typeof COMMAND_DELTAS[Command]][]) {
        const nx = x + delta.dx;
        const ny = y + delta.dy;
        const key = `${nx},${ny}`;

        if (
          nx >= 0 &&
          nx < mazeData.size &&
          ny >= 0 &&
          ny < mazeData.size &&
          mazeData.grid[ny][nx] === 0 &&
          !visited.has(key)
        ) {
          visited.add(key);
          queue.push([nx, ny, [...path, cmd]]);
        }
      }
    }

    return null;
  };

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const getCellClass = (x: number, y: number) => {
    if (!mazeData) return '';

    const classes = ['flex items-center justify-center transition-all'];

    if (mazeData.grid[y][x] === 1) {
      classes.push('bg-gray-700');
    } else {
      classes.push('bg-white');
    }

    if (currentPosition && currentPosition.x === x && currentPosition.y === y) {
      classes.push('bg-yellow-300 animate-pulse');
    } else if (mazeData.meta.x === x && mazeData.meta.y === y) {
      classes.push('bg-green-200');
    } else if (visitedCells.some(v => v.x === x && v.y === y)) {
      classes.push('bg-blue-100');
    }

    return classes.join(' ');
  };

  const getCellContent = (x: number, y: number) => {
    if (!mazeData) return '';

    if (currentPosition && currentPosition.x === x && currentPosition.y === y) {
      return mazeData.animal;
    }

    if (!currentPosition && mazeData.inicio.x === x && mazeData.inicio.y === y) {
      return mazeData.animal;
    }

    if (mazeData.meta.x === x && mazeData.meta.y === y) {
      return mazeData.objetivo;
    }

    const star = mazeData.estrellas.find(s => s.x === x && s.y === y);
    if (star && !star.collected) {
      return '⭐';
    }

    return '';
  };

  const getCellFontSize = () => {
    if (config.size <= 7) return 'text-3xl';
    if (config.size <= 9) return 'text-2xl';
    if (config.size <= 11) return 'text-xl';
    return 'text-lg';
  };

  return (
    <div
      className="h-full flex flex-col"
      style={{ background: tenant.theme.colors.gradients.main, fontFamily: tenant.theme.fonts.body.family }}
    >
      {/* Top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🗺️ Laberinto</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {Object.entries(LEVELS).map(([key, level]) => (
            <button
              key={key}
              onClick={() => setNivel(key as DifficultadKey)}
              disabled={isExecuting}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                nivel === key ? 'bg-white' : 'bg-white/20 text-white hover:bg-white/30'
              } disabled:opacity-50`}
              style={nivel === key ? { color: tenant.theme.colors.primary } : {}}
            >
              {level.icono} {level.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <span className="text-white text-xs font-semibold">
          🎯{attemptsCount} · ⭐{starsCollected}/{config.estrellas} · 📝{sequence.length}
        </span>
        {/* Rules */}
        <button
          onClick={() => setShowRules(!showRules)}
          title="Reglas"
          className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all text-sm"
        >
          📖
        </button>
        {/* New maze */}
        <button
          onClick={generateNewMaze}
          disabled={isExecuting}
          title="Nuevo Laberinto"
          className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* Rules panel */}
      {showRules && (
        <div className="bg-white/90 px-4 py-3 text-sm text-gray-800">
          <p className="font-bold mb-1">🗺️ Reglas del Laberinto</p>
          <p>1. Observa el laberinto completo. 2. Programa TODA la secuencia de movimientos. 3. Presiona "Ejecutar". 4. Si fallas, ajusta e intenta de nuevo.</p>
          <p className="mt-1">⭐ Recoge las estrellas · 🎯 Llega a la meta con el mínimo de intentos</p>
        </div>
      )}

      {/* Board */}
      <div className="flex-1 flex flex-col lg:flex-row items-center justify-center p-3 pb-safe gap-4">
        {/* Maze */}
        {mazeData && (
          <div className="flex justify-center">
            <div
              className={`laberinto-board grid gap-0.5 bg-gray-800 border-3 border-gray-800 rounded-xl overflow-hidden max-w-full ${getCellFontSize()}`}
              style={{
                gridTemplateColumns: `repeat(${config.size}, 1fr)`,
                width: 'min(500px, 90vw)',
                aspectRatio: '1/1'
              }}
            >
              {mazeData.grid.map((row, y) =>
                row.map((_, x) => (
                  <div key={`${x}-${y}`} className={getCellClass(x, y)}>
                    {getCellContent(x, y)}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Programming panel */}
        <div className="space-y-3 w-full max-w-sm">
          {/* Command buttons */}
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-3">
            <h3 className="text-center font-bold mb-3 text-white text-sm">Añade los comandos</h3>
            <div className="grid grid-cols-4 gap-2">
              {(['up', 'down', 'left', 'right'] as Command[]).map(cmd => (
                <button
                  key={cmd}
                  onClick={() => addCommand(cmd)}
                  disabled={isExecuting || gameWon}
                  className="aspect-square rounded-xl text-3xl font-bold text-white shadow-lg hover:scale-105 transition-all disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                >
                  {COMMAND_ARROWS[cmd]}
                </button>
              ))}
            </div>
          </div>

          {/* Sequence */}
          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-3">
            <h3 className="text-center font-bold mb-2 text-white text-sm">Tu secuencia</h3>
            <div className="min-h-[80px] max-h-[160px] overflow-y-auto">
              {sequence.length === 0 ? (
                <div className="text-center text-white/60 italic py-6 text-sm">
                  Haz clic en las flechas para añadir
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5 justify-center p-1">
                  {sequence.map((cmd, index) => (
                    <div
                      key={index}
                      onClick={() => removeCommand(index)}
                      className="relative w-10 h-10 bg-white/80 rounded-lg flex items-center justify-center text-xl font-bold cursor-pointer hover:bg-red-100 transition-all"
                      title="Haz clic para eliminar"
                    >
                      {COMMAND_ARROWS[cmd]}
                      <div
                        className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full text-white text-xs flex items-center justify-center"
                        style={{ background: tenant.theme.colors.primary, fontSize: '9px' }}
                      >
                        {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={executeSequence}
              disabled={sequence.length === 0 || isExecuting || gameWon}
              className="flex-1 px-4 py-2 rounded-xl font-bold text-white shadow-lg disabled:opacity-50 text-sm"
              style={{ background: '#38ef7d' }}
            >
              ▶️ Ejecutar
            </button>
            <button
              onClick={clearSequence}
              disabled={sequence.length === 0 || isExecuting || gameWon}
              className="flex-1 px-4 py-2 rounded-xl font-bold text-white shadow-lg disabled:opacity-50 text-sm"
              style={{ background: '#ef5350' }}
            >
              🗑️ Borrar
            </button>
            <button
              onClick={showHint}
              disabled={isExecuting || gameWon}
              className="flex-1 px-4 py-2 rounded-xl font-bold text-white shadow-lg disabled:opacity-50 text-sm"
              style={{ background: '#ffa726' }}
            >
              💡 Pista
            </button>
          </div>

          {gameWon && (
            <div className="text-center bg-white/20 backdrop-blur-sm rounded-2xl p-4">
              <div className="text-4xl mb-2">🎉</div>
              <h2 className="text-xl font-bold mb-2 text-white">¡Lo lograste!</h2>
              <p className="text-white/80 text-sm mb-1">
                {attemptsCount} {attemptsCount === 1 ? 'intento' : 'intentos'} · {sequence.length} {sequence.length === 1 ? 'comando' : 'comandos'}
              </p>
              {starsCollected > 0 && (
                <p className="text-white/80 text-sm mb-3">⭐ {starsCollected} {starsCollected === 1 ? 'estrella' : 'estrellas'}</p>
              )}
              <button
                onClick={generateNewMaze}
                className="px-6 py-2 rounded-xl font-bold text-white shadow-lg text-sm"
                style={{ background: tenant.theme.colors.primary }}
              >
                🔄 Nuevo Laberinto
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Executing overlay */}
      {isExecuting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white px-12 py-8 rounded-3xl shadow-2xl">
            <div className="text-3xl font-bold" style={{ color: tenant.theme.colors.primary }}>
              ⚙️ Ejecutando programa...
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
